import { useForm, useWatch } from "react-hook-form";
import Wizard from "../../components/Wizard";
import { Teacher } from "../../models/teacher/teacher";
import { School } from "../../models/school/school";
import DurationStep from "./wizard-steps/DurationStep";
import VacancyReasonStep from "./wizard-steps/VacancyReasonStep";
import DescriptionStep from "./wizard-steps/DescriptionStep";
import LessonsStep from "./wizard-steps/LessonsStep";
import { useEffect, useMemo, useState } from "react";
import { SubstituteSchedulerLesson } from "../../components/scheduler/SubstituteScheduler";
import SubstitutesStep from "./wizard-steps/SubstitutesStep";
import TeacherSelectionStep from "./wizard-steps/TeacherSelectionStep";
import { useQuery } from "react-query";
import {
  generateSubstituteComponentSchedule,
} from "../../helpers/utils/calendar.util";
import {
  validateEndDate,
  validateStartDate,
} from "./wizard-steps/validate-functions";
import { useSchedulerApi } from "../../apis/scheduler-api";
import { VacancyLesson, isVacancyLesson } from "../../models/schedule/lesson";
import { AbsenceReason, Vacancy } from "../../models/vacancy/vacancy";

export type VacancyFormFields = {
  teacher: Teacher;
  substitutes?: Teacher[];
  start: string;
  end: string;
  description: string;
  scheduleId: string;
  lessons: VacancyLesson[];
  absenceReason: AbsenceReason;
};

type VacancyEditWizardProps = {
  onSubmit: (data: VacancyFormFields) => void;
  preselectedTeacher?: Teacher;
  hideTeacherStep?: boolean;
  school: School;
  vacancy?: Vacancy;
};

function VacancyEditWizard({
  onSubmit,
  preselectedTeacher,
  school,
  hideTeacherStep,
  vacancy,
}: VacancyEditWizardProps) {
  const {
    register,
    control,
    formState: { errors, isValid },
    trigger,
    getValues,
    setValue,
  } = useForm<VacancyFormFields>({
    defaultValues: {
      teacher: preselectedTeacher ?? vacancy?.absentTeacher,
      absenceReason: vacancy?.absenceReason,
      description: vacancy?.description,
      start: vacancy?.startDate.toISOString().slice(0, 10),
      end: vacancy?.endDate.toISOString().slice(0, 10),
      scheduleId: vacancy?.schedule.id,
      substitutes: vacancy?.teacherAccepted,
    },
    mode: "onChange",
  });
  const [lessons, setLessons] = useState<SubstituteSchedulerLesson[][]>([]);
  const selectedTeacher = useWatch({ name: "teacher", control });
  const dateStrings = useWatch({ name: ["start", "end"], control });
  const dates = useMemo(
    () => dateStrings.map((date) => new Date(date)),
    [dateStrings],
  );
  const { getSchedule } = useSchedulerApi();
  const updateMode = useMemo(() => !!vacancy, [vacancy]);

  const { data: schedule } = useQuery(["schedule", school, selectedTeacher], {
    queryFn: () => getSchedule(school.id, selectedTeacher.id),
    enabled: !!school && !!selectedTeacher,
  });

  useEffect(() => {
    if (preselectedTeacher) {
      setValue("teacher", preselectedTeacher);
    }
  }, [preselectedTeacher, setValue]);

  useEffect(() => {
    if (
      dates[0] &&
      dates[1] &&
      schedule &&
      validateStartDate(dates[0], dates[1]) === undefined &&
      validateEndDate(dates[1], dates[0]) === undefined
    ) {
      const generatedLessons = generateSubstituteComponentSchedule(
        schedule,
        dates[0],
        dates[1],
        vacancy?.lessons,
        vacancy?.endDate
      );
      setLessons(generatedLessons);
    }
  }, [dates, schedule, vacancy]);

  useEffect(() => {
    if (schedule) {
      setValue("scheduleId", schedule.id);
    }
  }, [schedule, setValue]);

  const canActivateNextStep = async () => await trigger();

  const onFinish = () => {
    const formValues = getValues();

    onSubmit({
      ...formValues,
      lessons: lessons
        .flatMap((l) => l)
        .filter((l) => l.isActive)
        .filter((l) => isVacancyLesson(l)) as VacancyLesson[],
    });
  };

  return (
    <div className="flex min-h-full flex-grow overflow-hidden">
      <form className="flex-grow">
        <Wizard
          className="min-h-full"
          canActivateNextStep={canActivateNextStep}
          onFinish={onFinish}
          allowSkipSteps={false}
        >
          {!hideTeacherStep && !updateMode && (
            <Wizard.Step isValid={true} name="Lehrpersonen Auswahl">
              <TeacherSelectionStep
                control={control}
                register={register}
                errors={errors}
                school={school}
              />
            </Wizard.Step>
          )}
          <Wizard.Step isValid={true} name="Ausfalldauer">
            <DurationStep
              control={control}
              register={register}
              errors={errors}
              canEditStartDate={!vacancy}
            />
          </Wizard.Step>
          {!updateMode && (
            <Wizard.Step isValid={true} name="Ausfallgrund">
              <VacancyReasonStep
                control={control}
                register={register}
                errors={errors}
              />
            </Wizard.Step>
          )}
          <Wizard.Step
            isValid={lessons.flatMap((x) => x).some((l) => l.isActive)}
            name="Stundenplan"
          >
            {schedule && (
              <LessonsStep
                control={control}
                register={register}
                errors={errors}
                setLessons={setLessons}
                lessons={lessons}
                schedule={schedule}
              />
            )}
          </Wizard.Step>
          {!updateMode && (
            <Wizard.Step isValid={isValid} name="Ausfall-Vertretungen">
              <div className="flex h-full w-full items-center justify-center">
                {schedule && (
                  <SubstitutesStep
                    control={control}
                    register={register}
                    errors={errors}
                    schedule={schedule}
                    lessons={lessons}
                    setLessons={setLessons}
                    setValue={setValue}
                  />
                )}
              </div>
            </Wizard.Step>
          )}
          {!updateMode && (
            <Wizard.Step isValid={true} name="Weitere Informationen">
              <DescriptionStep
                control={control}
                register={register}
                errors={errors}
              />
            </Wizard.Step>
          )}
        </Wizard>
      </form>
    </div>
  );
}

export default VacancyEditWizard;
